import type { FurnitureEffect } from './furniture/types.js';
import type { LookSlot } from './look.js';

/**
 * Clothing and accessories that cost Kaki Dollars to unlock. Anything not
 * listed here is free to wear. Ownership is stored as an inventory item of
 * kind `wear:<slot>:<index>` (see `wearableKind`).
 *
 * Indices refer to the style tables in the client's palette.ts; index 0 is
 * always the free default, and hat 1 (the red cap) stays free for players
 * who have worn it since the first release.
 */
export interface WearableDef {
  slot: LookSlot;
  index: number;
  label: string;
  /** Kaki Dollars. Zero means it is only found in blindboxes, or sold for `premium` instead. */
  price: number;
  /**
   * Sold for Gold or Diamonds instead of Kaki Dollars, through the Rare shop.
   * Such items have `price: 0`; see `FurnitureDef.premium`, which this mirrors.
   */
  premium?: { currency: 'gold' | 'diamond'; price: number };
  /** A purely cosmetic animation drawn above the wearer while it is worn (rarest prizes only). */
  effect?: FurnitureEffect;
  /**
   * A unique piece never sold anywhere, not even for Gold or Diamonds: staff
   * grant it directly (`admin.js grant-item`). Hidden from the wardrobe grid
   * for anyone who does not already own it, so it stays a surprise.
   */
  giftOnly?: boolean;
}

export const WEARABLES: readonly WearableDef[] = Object.freeze([
  { slot: 'eyes', index: 5, label: 'Sparkle Eyes', price: 2000 },
  { slot: 'top', index: 3, label: 'Office Shirt & Tie', price: 400 },
  { slot: 'top', index: 4, label: 'Hoodie', price: 600 },
  { slot: 'top', index: 5, label: 'Kebaya Blouse', price: 900 },
  { slot: 'top', index: 6, label: 'Football Jersey', price: 500 },
  { slot: 'bottom', index: 3, label: 'Sarong', price: 350 },
  { slot: 'shoes', index: 2, label: 'Boots', price: 450 },
  { slot: 'hat', index: 3, label: 'Straw Hat', price: 300 },
  { slot: 'hat', index: 4, label: 'Chef Hat', price: 800 },
  { slot: 'hat', index: 5, label: 'Hard Hat', price: 500 },
  { slot: 'hat', index: 6, label: 'Headphones', price: 1200 },
  { slot: 'glasses', index: 2, label: 'Shades', price: 600 },
  { slot: 'glasses', index: 4, label: 'Heart Glasses', price: 1500 },
  // Blindbox prizes: price 0 means not sold anywhere (see blindbox.ts).
  { slot: 'hat', index: 8, label: 'Party Hat', price: 0 },
  { slot: 'hat', index: 9, label: 'Top Hat', price: 0 },
  { slot: 'hat', index: 10, label: 'Golden Crown', price: 0, effect: { type: 'sparkle', color: '#f0bf36', color2: '#fffaf0', height: 9 } },
  // Second wardrobe expansion: appended only, existing indices are untouched.
  { slot: 'top', index: 8, label: 'Batik Shirt', price: 700 },
  { slot: 'top', index: 9, label: 'Varsity Jacket', price: 750 },
  { slot: 'shoes', index: 5, label: 'Heels', price: 400 },
  { slot: 'hat', index: 11, label: 'Beret', price: 350 },
  { slot: 'glasses', index: 5, label: 'Cat-eye Glasses', price: 650 },
  // Blindbox-exclusive prizes (price 0): epic ones carry an effect.
  { slot: 'eyes', index: 7, label: 'Starlight Eyes', price: 0, effect: { type: 'sparkle', color: '#8a52c8', color2: '#ffffff', height: 5 } },
  { slot: 'glasses', index: 6, label: 'Monocle', price: 0, effect: { type: 'glow', color: '#f0bf36', color2: '#fff4c2', height: 3 } },
  { slot: 'bottom', index: 5, label: 'Overalls', price: 0 },
  { slot: 'hat', index: 13, label: 'Firecracker Crown', price: 0, effect: { type: 'flames', color: '#d9433b', color2: '#ffd166', height: 10 } },
  { slot: 'glasses', index: 7, label: 'Diamond Shades', price: 0, effect: { type: 'glow', color: '#7fe0ff', color2: '#ffffff', height: 3 } },
  // Unique gifts: held props, never sold, granted one at a time by staff.
  { slot: 'prop', index: 1, label: 'Red Lightsaber', price: 0, giftOnly: true },
  { slot: 'prop', index: 2, label: 'Blue Lightsaber', price: 0, giftOnly: true },
  { slot: 'prop', index: 3, label: 'Green Lightsaber', price: 0, giftOnly: true },
  { slot: 'prop', index: 4, label: 'Purple Lightsaber', price: 0, giftOnly: true },
  { slot: 'prop', index: 5, label: 'Pink Lightsaber', price: 0, giftOnly: true },
  { slot: 'prop', index: 6, label: 'White Lightsaber', price: 0, giftOnly: true },
]);

export function wearableKind(slot: LookSlot, index: number): string {
  return `wear:${slot}:${index}`;
}

export function findWearable(slot: LookSlot, index: number): WearableDef | undefined {
  return WEARABLES.find((w) => w.slot === slot && w.index === index);
}

/** Parses a `wear:<slot>:<index>` kind back into its catalogue entry. */
export function wearableFromKind(kind: string): WearableDef | undefined {
  const match = /^wear:([a-zA-Z]+):(\d{1,2})$/.exec(kind);
  if (!match) return undefined;
  return WEARABLES.find((w) => w.slot === match[1] && w.index === Number(match[2]));
}

/* -------------------------------------------------------------------------- */
/* Player designs                                                             */
/* -------------------------------------------------------------------------- */

/** Designs are square pixel grids printed on the front of a top. */
export const DESIGN_SIZE = 12;

/**
 * The fixed 16-colour design palette. A design is a string of
 * DESIGN_SIZE * DESIGN_SIZE characters, row by row: '.' is transparent and
 * '0'-'9', 'a'-'f' pick a palette entry. Never reorder: stored designs index it.
 */
export const DESIGN_PALETTE: readonly string[] = Object.freeze([
  '#1d1716',
  '#fffaf0',
  '#d9433b',
  '#ef7d2c',
  '#f0bf36',
  '#8fcf6a',
  '#3d9e4c',
  '#2c9f9b',
  '#7fb7d9',
  '#3a6fd6',
  '#8a52c8',
  '#f19bb7',
  '#6b4226',
  '#c9a27a',
  '#8a8a8a',
  '#fbe0c6',
]);

export const DESIGN_PIXELS_PATTERN = /^[.0-9a-f]{144}$/;
export const MAX_DESIGN_NAME_LENGTH = 24;
export const MAX_DESIGNS_PER_PLAYER = 20;
/** Cost of each extra copy of a design, for selling. */
export const DESIGN_MINT_PRICE = 20;

export function isValidDesignPixels(pixels: string): boolean {
  return DESIGN_PIXELS_PATTERN.test(pixels) && /[0-9a-f]/.test(pixels);
}

export function designKind(designId: string): string {
  return `design:${designId}`;
}

export function designIdFromKind(kind: string): string | null {
  return kind.startsWith('design:') ? kind.slice(7) : null;
}
